import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { alpha, Avatar, Box, Button, Chip, IconButton, Skeleton, Tooltip, Typography } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';

import { Item } from '../../../styles/common';
import { contactPath } from '../crmPaths';
import { countryName, describeSupportLevel } from '../contacts/contactList';
import { displayName, entryFacts, formatDay, personName, phoneText, TimelineEntry } from '../contactDetail/contactDetail';
import { CRM_QUEUE_RECENT_CALLS } from './callQueue.queries';
import { describeReason, dialOrder, numbersOf, QueueItem, QueueReason, REASON_WORDS, telHref } from './callQueue';

const REASON_TONES: Record<QueueReason, { bg: string; fg: string }> = {
  FOLLOW_UP_DUE: { bg: '#fef3c7', fg: '#92400e' },
  NEVER_CALLED: { bg: '#dbeafe', fg: '#1e40af' },
  NOT_CALLED_RECENTLY: { bg: '#e0e7ff', fg: '#3730a3' },
};

const initialsOf = (name: string) => {
  const words = name.split(/\s+/).filter(Boolean);
  return `${words[0]?.[0] ?? '#'}${words.length > 1 ? words[words.length - 1][0] : ''}`.toUpperCase();
};

/** Label and value, one row each. Empty values are left out: a caller has no time for dashes. */
const Facts = ({ rows }: { rows: Array<[string, React.ReactNode]> }) => (
  <Box
    component="dl"
    sx={{ m: 0, display: 'grid', gridTemplateColumns: 'minmax(96px, max-content) 1fr', columnGap: 2, rowGap: 0.5 }}
  >
    {rows
      .filter(([, value]) => value !== '' && value != null)
      .map(([label, value]) => (
        <React.Fragment key={label}>
          <Typography component="dt" variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography component="dd" variant="body2" sx={{ m: 0, minWidth: 0, wordBreak: 'break-word' }}>
            {value}
          </Typography>
        </React.Fragment>
      ))}
  </Box>
);

const RecentCalls = ({ contactId }: { contactId: number }) => {
  const { data, loading, error } = useQuery<{ crmContactTimeline: { items: Array<TimelineEntry> } }>(CRM_QUEUE_RECENT_CALLS, {
    variables: { contactId },
    fetchPolicy: 'network-only',
  });
  const items = data?.crmContactTimeline.items ?? [];

  if (loading && !data) {
    return (
      <Box role="status" aria-label="Loading their calls…">
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="80%" />
      </Box>
    );
  }
  if (error) {
    return (
      <Typography variant="body2" color="text.secondary">
        Their earlier calls could not be loaded. They are on their page.
      </Typography>
    );
  }
  if (!items.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No calls on record. This is the first.
      </Typography>
    );
  }
  return (
    <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }}>
      {items.map((entry) => {
        const facts = entryFacts(entry);
        return (
          <Box component="li" key={entry.id} sx={{ borderLeft: '3px solid #bfdbfe', pl: 1.25 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {entry.title}{' '}
              <Typography component="time" variant="caption" color="text.secondary" dateTime={entry.occurredAt}>
                {formatDay(entry.occurredAt)}
              </Typography>
            </Typography>
            {facts.length > 0 && (
              <Typography variant="caption" color="text.secondary" component="p">
                {facts.join(' · ')}
              </Typography>
            )}
            {entry.body && (
              <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {entry.body}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

interface QueuePersonProps {
  item: QueueItem;
  /** Dialling a number starts the call timer. */
  onDial: () => void;
  /** Said once a number is copied. */
  onCopied: (text: string) => void;
}

/**
 * Who the caller is ringing (card 21): the numbers to dial, why they were
 * served, and enough about them to open the call. Their full page opens in a
 * new tab, so the queue keeps its place. What was said before is
 * QueueHistory, laid out apart so a phone shows the form before it.
 */
export const QueuePerson = ({ item, onDial, onCopied }: QueuePersonProps) => {
  const contact = item.contact;
  if (!contact) return null;

  const name = displayName(contact);
  const phones = dialOrder(numbersOf(contact));
  const reason = item.reason;
  const tone = reason ? REASON_TONES[reason] : REASON_TONES.NEVER_CALLED;
  const place = [contact.city, contact.region, contact.countryCode ? countryName(contact.countryCode) : null].filter(Boolean).join(', ');

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      onCopied(text);
    } catch {
      // No clipboard (an insecure page, an old browser): the number is on screen to read.
    }
  };

  return (
    <Item sx={{ p: { xs: 1.5, sm: 2.5 }, minWidth: 0 }}>
      <Box component="section" aria-label={`Calling ${name}`}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
          <Avatar
            aria-hidden
            sx={(theme) => ({
              width: 52,
              height: 52,
              fontWeight: 700,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
            })}
          >
            {initialsOf(name)}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h5" component="h2" sx={{ lineHeight: 1.25, wordBreak: 'break-word' }}>
                {name}
              </Typography>
              {reason && (
                <Chip size="small" label={REASON_WORDS[reason]} sx={{ fontWeight: 700, bgcolor: tone.bg, color: tone.fg, height: 22 }} />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {describeReason(item, dayjs())}
            </Typography>
          </Box>
          <Tooltip describeChild title="Their whole record, in a new tab">
            <Button
              component="a"
              href={contactPath(contact.id)}
              target="_blank"
              rel="noopener"
              size="small"
              endIcon={<OpenInNewIcon fontSize="small" />}
              sx={{ flexShrink: 0, display: { xs: 'none', sm: 'inline-flex' } }}
            >
              Their page
            </Button>
          </Tooltip>
        </Box>

        {item.followUp && reason === 'FOLLOW_UP_DUE' && (
          <Box sx={{ display: 'flex', gap: 1, bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 1.5, px: 1.5, py: 1, mb: 1.5 }}>
            <EventRepeatOutlinedIcon sx={{ color: '#b45309', fontSize: 20, mt: 0.25 }} aria-hidden />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Why call back
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {item.followUp.notes || 'No notes were left with the follow-up.'}
              </Typography>
            </Box>
          </Box>
        )}

        <Box role="group" aria-label="Their numbers" sx={{ display: 'grid', gap: 1, mb: 2 }}>
          {phones.length === 0 && (
            <Typography variant="body2" color="error">
              No phone number on record.
            </Typography>
          )}
          {phones.map((phone, index) => {
            const href = telHref(phone);
            const text = phoneText(phone);
            const main = index === 0 && Boolean(href);
            return (
              <Box key={phone.id ?? 'main'} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {href ? (
                  <Button
                    component="a"
                    href={href}
                    onClick={onDial}
                    variant={main ? 'contained' : 'outlined'}
                    size={main ? 'large' : 'medium'}
                    startIcon={<PhoneOutlinedIcon />}
                    aria-label={`Call ${text}${phone.type ? `, ${phone.type.toLowerCase()}` : ''}`}
                    sx={{
                      fontVariantNumeric: 'tabular-nums',
                      letterSpacing: '0.02em',
                      fontSize: main ? '1.25rem' : '1rem',
                      textTransform: 'none',
                      py: main ? 1 : 0.5,
                    }}
                  >
                    {text}
                  </Button>
                ) : (
                  <Typography
                    variant="body1"
                    sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary', ...(phone.isBad ? { textDecoration: 'line-through' } : {}) }}
                  >
                    {text}
                  </Typography>
                )}
                <Tooltip title="Copy the number">
                  <IconButton size="small" aria-label={`Copy ${text}`} onClick={() => copy(text)}>
                    <ContentCopyOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {phone.type && (
                  <Typography variant="caption" color="text.secondary">
                    {phone.type.toLowerCase()}
                  </Typography>
                )}
                {phone.isBad && <Chip size="small" color="error" variant="outlined" label="Marked bad" sx={{ height: 20 }} />}
                {!phone.e164 && (
                  <Tooltip title="NationBuilder's number could not be read as an international number. Dial it by hand.">
                    <Chip size="small" color="warning" variant="outlined" label="Not diallable" sx={{ height: 20 }} />
                  </Tooltip>
                )}
              </Box>
            );
          })}
        </Box>

        <Facts
          rows={[
            ['From', place],
            ['Support', contact.supportLevel ? describeSupportLevel(contact.supportLevel) : ''],
            ['Stage', contact.lifecycleStage ?? ''],
            ['Point person', personName(contact.owner)],
            ['Email', contact.primaryEmail ?? ''],
            ['App', contact.account ? 'Has the app' : ''],
            ['Calls', contact.callCount ? `${contact.callCount.toLocaleString('en-US')}, last ${formatDay(contact.lastCalledAt)}` : 'None yet'],
          ]}
        />

        {contact.tags.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }} aria-label="Tags" role="list">
            {contact.tags.slice(0, 12).map((tag) => (
              <Chip
                key={tag.tagId}
                role="listitem"
                size="small"
                label={tag.name}
                variant="outlined"
                sx={{ height: 22, borderColor: tag.color ? alpha(tag.color, 0.5) : undefined, color: tag.color ?? undefined }}
              />
            ))}
            {contact.tags.length > 12 && (
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                and {contact.tags.length - 12} more
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Item>
  );
};

/** What was said before: their last calls, and their notes. */
export const QueueHistory = ({ item }: { item: QueueItem }) => {
  const contact = item.contact;
  const [allNotes, setAllNotes] = useState(false);
  if (!contact) return null;
  const notes = allNotes ? contact.notes : contact.notes.slice(0, 2);

  return (
    <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
      <Item sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Box component="section" aria-labelledby="crm-queue-recent">
          <Typography id="crm-queue-recent" variant="subtitle1" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
            Last calls
          </Typography>
          <RecentCalls contactId={contact.id} />
        </Box>
      </Item>

      {contact.notes.length > 0 && (
        <Item sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box component="section" aria-labelledby="crm-queue-notes">
            <Typography id="crm-queue-notes" variant="subtitle1" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
              {contact.notes.length > 1 ? 'Notes' : 'Note'}
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }}>
              {notes.map((note) => (
                <Box component="li" key={note.id} sx={{ bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 1.5, px: 1.25, py: 0.75 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {note.body}
                  </Typography>
                </Box>
              ))}
            </Box>
            {contact.notes.length > 2 && (
              <Button size="small" onClick={() => setAllNotes(!allNotes)} sx={{ mt: 0.5 }}>
                {allNotes ? 'Fewer notes' : `All ${contact.notes.length} notes`}
              </Button>
            )}
          </Box>
        </Item>
      )}
    </Box>
  );
};
